import jsPDF from 'jspdf';

/**
 * Professional Roadmap PDF Generator
 * Creates a clean visual roadmap with a vertical timeline and organized cards
 */

// Color palette
const COLORS = {
  primary: '#10B981',
  primaryLight: '#34D399',
  primaryDark: '#059669',
  background: '#0F172A',
  backgroundLight: '#1E293B',
  backgroundCard: '#1E293B',
  text: '#F8FAFC',
  textMuted: '#94A3B8',
  certification: '#F59E0B',
  certificationLight: '#FCD34D',
  border: '#334155',
};

// Helper to convert hex to RGB
function hexToRgb(hex) {
  const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
  return result ? {
    r: parseInt(result[1], 16),
    g: parseInt(result[2], 16),
    b: parseInt(result[3], 16)
  } : { r: 0, g: 0, b: 0 };
}

function setFillColor(pdf, hex) {
  const { r, g, b } = hexToRgb(hex);
  pdf.setFillColor(r, g, b);
}

function setDrawColor(pdf, hex) {
  const { r, g, b } = hexToRgb(hex);
  pdf.setDrawColor(r, g, b);
}

function setTextColor(pdf, hex) {
  const { r, g, b } = hexToRgb(hex);
  pdf.setTextColor(r, g, b);
}

function drawRoundedRect(pdf, x, y, w, h, r, style = 'F') {
  pdf.roundedRect(x, y, w, h, r, r, style);
}

// Draw page background
function drawBackground(pdf, pageWidth, pageHeight) {
  setFillColor(pdf, COLORS.background);
  pdf.rect(0, 0, pageWidth, pageHeight, 'F');
}

// Draw header
function drawHeader(pdf, title, subtitle, pageWidth, totalMonths) {
  // Header background
  setFillColor(pdf, COLORS.backgroundLight);
  pdf.rect(0, 0, pageWidth, 85, 'F');

  // Green accent line
  setFillColor(pdf, COLORS.primary);
  pdf.rect(0, 83, pageWidth, 2, 'F');

  // Logo
  setFillColor(pdf, COLORS.primary);
  drawRoundedRect(pdf, 25, 15, 50, 50, 10, 'F');

  pdf.setFont('helvetica', 'bold');
  pdf.setFontSize(20);
  setTextColor(pdf, COLORS.background);
  pdf.text('CA', 38, 47);

  // Title
  pdf.setFontSize(22);
  setTextColor(pdf, COLORS.text);
  const displayTitle = title || 'Your Career Roadmap';
  pdf.text(displayTitle, 90, 38);

  // Subtitle
  if (subtitle) {
    pdf.setFont('helvetica', 'normal');
    pdf.setFontSize(10);
    setTextColor(pdf, COLORS.textMuted);
    pdf.text(subtitle, 90, 52);
  }

  // Duration badge
  pdf.setFont('helvetica', 'bold');
  pdf.setFontSize(9);
  setTextColor(pdf, COLORS.primaryLight);
  pdf.text(`${totalMonths} Month Journey`, 90, 68);

  // Branding
  setTextColor(pdf, COLORS.textMuted);
  pdf.text('CYBER AGOGE', pageWidth - 90, 68);

  return 100;
}

// Draw footer
function drawFooter(pdf, pageWidth, pageHeight, pageNum, totalPages) {
  const footerY = pageHeight - 25;

  setFillColor(pdf, COLORS.backgroundLight);
  pdf.rect(0, footerY - 5, pageWidth, 30, 'F');

  setFillColor(pdf, COLORS.primary);
  pdf.rect(0, footerY - 5, pageWidth, 1, 'F');

  pdf.setFont('helvetica', 'normal');
  pdf.setFontSize(8);
  setTextColor(pdf, COLORS.textMuted);
  pdf.text('Generated by Cyber Agoge | www.cyberagoge.com', 25, footerY + 10);

  pdf.text(`Page ${pageNum} of ${totalPages}`, pageWidth - 25, footerY + 10, { align: 'right' });
}

// Draw the vertical timeline
function drawTimeline(pdf, startY, endY, centerX) {
  // Main line
  setDrawColor(pdf, COLORS.primary);
  pdf.setLineWidth(4);
  pdf.line(centerX, startY, centerX, endY);

  // Glow effect (lighter line behind)
  setDrawColor(pdf, COLORS.primaryLight);
  pdf.setLineWidth(8);
  pdf.line(centerX, startY, centerX, endY);

  // Main line on top
  setDrawColor(pdf, COLORS.primary);
  pdf.setLineWidth(4);
  pdf.line(centerX, startY, centerX, endY);
}

// Draw a month marker on the timeline
function drawMonthMarker(pdf, x, y, monthNum) {
  // White background circle
  setFillColor(pdf, COLORS.backgroundLight);
  setDrawColor(pdf, COLORS.primary);
  pdf.setLineWidth(3);
  pdf.circle(x, y, 18, 'FD');

  // Inner circle
  setFillColor(pdf, COLORS.primary);
  pdf.circle(x, y, 12, 'F');

  // Month number
  pdf.setFont('helvetica', 'bold');
  pdf.setFontSize(11);
  setTextColor(pdf, COLORS.text);
  pdf.text(String(monthNum), x, y + 4, { align: 'center' });
}

// Draw an item card
function drawItemCard(pdf, item, x, y, width, isLeft, timelineX) {
  const isCert = item?.type === 'certification';
  const cardHeight = 70;
  const borderColor = isCert ? COLORS.certification : COLORS.border;

  // Card background
  setFillColor(pdf, COLORS.backgroundCard);
  setDrawColor(pdf, borderColor);
  pdf.setLineWidth(isCert ? 2 : 1);
  drawRoundedRect(pdf, x, y, width, cardHeight, 8, 'FD');

  // Left accent bar
  const accentColor = isCert ? COLORS.certification : COLORS.primary;
  setFillColor(pdf, accentColor);
  drawRoundedRect(pdf, x, y, 5, cardHeight, 8, 'F');
  pdf.rect(x + 3, y, 5, cardHeight, 'F'); // Square off right edge

  // Connection line
  setDrawColor(pdf, COLORS.border);
  pdf.setLineWidth(1);
  pdf.setLineDashPattern([3, 3], 0);
  const lineY = y + cardHeight / 2;
  if (isLeft) {
    pdf.line(x + width, lineY, timelineX - 20, lineY);
  } else {
    pdf.line(x, lineY, timelineX + 20, lineY);
  }
  pdf.setLineDashPattern([], 0);

  // Small dot at line end
  setFillColor(pdf, COLORS.primaryLight);
  const dotX = isLeft ? timelineX - 20 : timelineX + 20;
  pdf.circle(dotX, lineY, 3, 'F');

  // Content
  const contentX = x + 15;
  const contentWidth = width - 25;

  // Title
  pdf.setFont('helvetica', 'bold');
  pdf.setFontSize(11);
  setTextColor(pdf, COLORS.text);
  const title = item?.title || item?.name || 'Untitled';
  const titleLines = pdf.splitTextToSize(title, contentWidth);
  pdf.text(titleLines[0], contentX, y + 18);

  // Certification badge
  let yOffset = 0;
  if (isCert) {
    pdf.setFontSize(7);
    pdf.setFont('helvetica', 'bold');
    setFillColor(pdf, '#451A03');
    const badgeText = 'CERTIFICATION';
    const badgeWidth = pdf.getTextWidth(badgeText) + 8;
    drawRoundedRect(pdf, contentX, y + 23, badgeWidth, 12, 2, 'F');
    setTextColor(pdf, COLORS.certificationLight);
    pdf.text(badgeText, contentX + 4, y + 31);
    yOffset = 15;
  }

  // Description
  if (item?.description) {
    pdf.setFont('helvetica', 'normal');
    pdf.setFontSize(8);
    setTextColor(pdf, COLORS.textMuted);
    const descLines = pdf.splitTextToSize(item.description, contentWidth);
    const desc = descLines.slice(0, 2).join(' ');
    pdf.text(desc.substring(0, 70) + (desc.length > 70 ? '...' : ''), contentX, y + 30 + yOffset, { maxWidth: contentWidth });
  }

  // Footer info
  pdf.setFont('helvetica', 'bold');
  pdf.setFontSize(8);
  setTextColor(pdf, COLORS.primaryLight);
  const duration = item?.duration ? `${item.duration}w` : '';
  const months = item?.startMonth && item?.endMonth ? `M${item.startMonth}-${item.endMonth}` : '';
  const cost = item?.cost ? `£${item.cost}` : '';
  const info = [duration, months, cost].filter(Boolean).join(' · ');
  pdf.text(info, contentX, y + cardHeight - 10);

  return cardHeight;
}

// Draw start marker
function drawStartMarker(pdf, x, y) {
  // Flag pole
  setFillColor(pdf, COLORS.textMuted);
  pdf.rect(x - 1, y - 20, 2, 30, 'F');

  // Flag
  setFillColor(pdf, COLORS.primary);
  pdf.triangle(x + 1, y - 20, x + 20, y - 12, x + 1, y - 4, 'F');

  // Label
  pdf.setFont('helvetica', 'bold');
  pdf.setFontSize(9);
  setTextColor(pdf, COLORS.primaryLight);
  pdf.text('START', x, y + 22, { align: 'center' });
}

// Draw finish marker
function drawFinishMarker(pdf, x, y) {
  // Outer circle
  setFillColor(pdf, COLORS.backgroundLight);
  setDrawColor(pdf, COLORS.primary);
  pdf.setLineWidth(4);
  pdf.circle(x, y, 22, 'FD');

  // Inner circle
  setFillColor(pdf, COLORS.primary);
  pdf.circle(x, y, 15, 'F');

  // Checkmark
  setDrawColor(pdf, COLORS.text);
  pdf.setLineWidth(3);
  pdf.setLineCap('round');
  pdf.line(x - 6, y, x - 2, y + 5);
  pdf.line(x - 2, y + 5, x + 7, y - 5);

  // Label
  pdf.setFont('helvetica', 'bold');
  pdf.setFontSize(10);
  setTextColor(pdf, COLORS.primaryLight);
  pdf.text('COMPLETE', x, y + 38, { align: 'center' });
}

// Main export function
export async function generateRoadmapPdf({ roadmap, title, subtitle, fileName = 'career-roadmap.pdf' }) {
  const pdf = new jsPDF('p', 'pt', 'a4');
  const pageWidth = pdf.internal.pageSize.getWidth();
  const pageHeight = pdf.internal.pageSize.getHeight();

  const items = Array.isArray(roadmap?.items) ? roadmap.items : [];
  const totalMonths = Number(roadmap?.totalMonths) || 6;

  // Group items by month
  const monthGroups = [];
  for (let m = 1; m <= totalMonths; m++) {
    const monthItems = items.filter(item => {
      if (item?.startMonth !== undefined && item?.endMonth !== undefined) {
        return item.startMonth <= m && item.endMonth >= m;
      }
      return (item?.month ?? 0) === m;
    }).sort((a, b) => (a.order ?? Infinity) - (b.order ?? Infinity));

    if (monthItems.length > 0) {
      monthGroups.push({ month: m, items: monthItems });
    }
  }

  // Layout constants
  const timelineX = pageWidth / 2;
  const cardWidth = 200;
  const leftCardX = timelineX - cardWidth - 40;
  const rightCardX = timelineX + 40;
  const cardSpacing = 85;

  // Calculate pages
  let totalItemCount = 0;
  monthGroups.forEach(g => totalItemCount += g.items.length);
  const itemsPerPage = 6;
  const totalPages = Math.max(1, Math.ceil(totalItemCount / itemsPerPage));

  let currentPage = 1;
  let itemsOnPage = 0;
  let isLeftSide = true;

  // First page
  drawBackground(pdf, pageWidth, pageHeight);
  let startY = drawHeader(pdf, title, subtitle, pageWidth, totalMonths);
  let currentY = startY + 30;

  // Draw start marker
  drawStartMarker(pdf, timelineX, currentY);
  currentY += 50;

  // Track timeline range for this page
  let timelineStartY = currentY;
  let timelineEndY = currentY;

  for (const group of monthGroups) {
    for (let i = 0; i < group.items.length; i++) {
      const item = group.items[i];

      // Check if need new page
      if (currentY + cardSpacing > pageHeight - 60) {
        // Draw timeline for current page
        drawTimeline(pdf, timelineStartY - 30, timelineEndY + 30, timelineX);
        drawFooter(pdf, pageWidth, pageHeight, currentPage, totalPages);

        // New page
        pdf.addPage();
        currentPage++;
        drawBackground(pdf, pageWidth, pageHeight);
        currentY = 40;
        timelineStartY = currentY;
        itemsOnPage = 0;
      }

      // Draw month marker for first item of each month
      if (i === 0) {
        drawMonthMarker(pdf, timelineX, currentY, group.month);
        currentY += 15;
      }

      // Draw the card
      const cardX = isLeftSide ? leftCardX : rightCardX;
      const cardY = currentY;

      drawItemCard(pdf, item, cardX, cardY, cardWidth, isLeftSide, timelineX);

      currentY += cardSpacing;
      timelineEndY = currentY;
      isLeftSide = !isLeftSide;
      itemsOnPage++;
    }
  }

  // Final destination
  if (currentY + 80 > pageHeight - 60) {
    drawTimeline(pdf, timelineStartY - 30, timelineEndY + 30, timelineX);
    drawFooter(pdf, pageWidth, pageHeight, currentPage, totalPages);

    pdf.addPage();
    currentPage++;
    drawBackground(pdf, pageWidth, pageHeight);
    currentY = 60;
    timelineStartY = currentY;
  }

  drawFinishMarker(pdf, timelineX, currentY + 20);
  timelineEndY = currentY + 20;

  // Draw timeline and footer for last page
  drawTimeline(pdf, timelineStartY - 30, timelineEndY, timelineX);
  drawFooter(pdf, pageWidth, pageHeight, currentPage, currentPage);

  pdf.save(fileName);
  return { success: true, pages: currentPage };
}

export default generateRoadmapPdf;
